import axios from 'axios';
import {
  BannerInstruction,
  DirectionsRequestBody,
  GeocodeResult,
  RouteAssetsReport,
  RouteEventsReport,
  RouteLeg,
  RouteResult,
  RouteRiskReport,
  VoiceInstruction,
  Waypoint,
} from '../types';
import { legVertexOffsetsFromSteps } from '../geo/insertionIndex';

const baseUrl = process.env.REACT_APP_API_URL || '/api';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
});

export interface DirectionsResponse {
  code: string;
  message?: string;
  routes: RouteResult[];
  /** Vertex offsets per route, parallel to `routes`. */
  legOffsets: number[][];
  cached: boolean;
}

interface RawStep {
  distance: number;
  duration: number;
  name?: string;
  geometry?: { coordinates?: number[][] };
  maneuver?: { instruction?: string; type?: string; modifier?: string; location?: [number, number] };
  voiceInstructions?: VoiceInstruction[];
  bannerInstructions?: BannerInstruction[];
}

interface RawLeg {
  distance: number;
  duration: number;
  duration_typical?: number;
  summary?: string;
  steps?: RawStep[];
  annotation?: { congestion_numeric?: (number | null)[] };
}

interface RawRoute {
  distance: number;
  duration: number;
  duration_typical?: number;
  weight_name?: string;
  geometry: GeoJSON.LineString;
  legs: RawLeg[];
}

function normalizeLeg(leg: RawLeg): RouteLeg {
  return {
    distance: leg.distance,
    duration: leg.duration,
    durationTypical: leg.duration_typical,
    summary: leg.summary || '',
    steps: (leg.steps || []).map((s) => ({
      distance: s.distance,
      duration: s.duration,
      name: s.name || '',
      instruction: s.maneuver?.instruction || '',
      type: s.maneuver?.type || 'continue',
      modifier: s.maneuver?.modifier,
      location: (s.maneuver?.location || [0, 0]) as [number, number],
      // Carried through rather than read here: nothing in the planner speaks,
      // but the Mission Navigator does, and the route it speaks is whatever
      // was persisted when the analyst planned it.
      voiceInstructions: s.voiceInstructions,
      bannerInstructions: s.bannerInstructions,
    })),
  };
}

/**
 * Congestion annotations are per *segment* and reported per leg. Flatten them
 * into one array aligned with the route's own vertex list so the line-gradient
 * paint can index straight into it.
 */
function flattenCongestion(legs: RawLeg[], vertexCount: number): (number | null)[] | undefined {
  const values: (number | null)[] = [];
  let sawAny = false;
  for (const leg of legs) {
    const legValues = leg.annotation?.congestion_numeric;
    if (!legValues) continue;
    sawAny = true;
    values.push(...legValues);
  }
  if (!sawAny || values.length === 0) return undefined;
  // Segments = vertices - 1; pad or trim rather than misalign the gradient.
  const target = Math.max(0, vertexCount - 1);
  if (values.length > target) return values.slice(0, target);
  while (values.length < target) values.push(null);
  return values;
}

function normalizeRoute(raw: RawRoute, index: number): { route: RouteResult; offsets: number[] } {
  const coordinates = (raw.geometry?.coordinates || []) as [number, number][];
  return {
    route: {
      id: `route-${index}`,
      distance: raw.distance,
      duration: raw.duration,
      durationTypical: raw.duration_typical,
      weightName: raw.weight_name,
      geometry: raw.geometry,
      legs: (raw.legs || []).map(normalizeLeg),
      congestion: flattenCongestion(raw.legs || [], coordinates.length),
    },
    offsets: legVertexOffsetsFromSteps(raw.legs || [], coordinates.length),
  };
}

export function buildDirectionsBody(
  waypoints: Waypoint[],
  options: { profile: DirectionsRequestBody['profile']; exclude: Record<string, boolean>; departAt: string | null },
): DirectionsRequestBody {
  const exclude = Object.entries(options.exclude)
    .filter(([, on]) => on)
    .map(([key]) => key);
  return {
    profile: options.profile,
    coordinates: waypoints.map((w) => ({ longitude: w.longitude, latitude: w.latitude })),
    alternatives: true,
    exclude,
    depart_at: options.departAt,
  };
}

/** Raw first route, including Mapbox legs the navigator speaks from. */
export async function fetchPrimaryDirections(
  body: DirectionsRequestBody,
  signal?: AbortSignal,
): Promise<{
  geometry: GeoJSON.LineString;
  distance?: number;
  duration?: number;
  legs?: unknown[];
} | null> {
  const response = await axios.post(`${baseUrl}/routing/directions`, body, {
    headers: authHeaders(),
    signal,
  });
  const raw = response.data?.routes?.[0];
  if (!raw?.geometry?.coordinates?.length) return null;
  return {
    geometry: raw.geometry,
    distance: raw.distance,
    duration: raw.duration,
    legs: raw.legs,
  };
}

export async function fetchDirections(
  body: DirectionsRequestBody,
  signal?: AbortSignal,
): Promise<DirectionsResponse> {
  const response = await axios.post(`${baseUrl}/routing/directions`, body, {
    headers: authHeaders(),
    signal,
  });
  const data = response.data;
  const normalized = (data.routes || []).map((r: RawRoute, i: number) => normalizeRoute(r, i));
  return {
    code: data.code || 'Ok',
    message: data.message,
    routes: normalized.map((n: { route: RouteResult }) => n.route),
    legOffsets: normalized.map((n: { offsets: number[] }) => n.offsets),
    cached: Boolean(data.cached),
  };
}

export async function searchPlaces(
  query: string,
  proximity?: { longitude: number; latitude: number } | null,
  signal?: AbortSignal,
): Promise<GeocodeResult[]> {
  const response = await axios.post(
    `${baseUrl}/routing/geocode`,
    { query, proximity: proximity || undefined, limit: 6 },
    { headers: authHeaders(), signal },
  );
  return response.data.results || [];
}

export async function reverseGeocode(
  coordinates: Array<{ longitude: number; latitude: number }>,
  signal?: AbortSignal,
): Promise<Array<{ longitude: number; latitude: number; label?: string; address?: string }>> {
  const response = await axios.post(
    `${baseUrl}/routing/reverse`,
    { coordinates },
    { headers: authHeaders(), signal },
  );
  return response.data.results || [];
}

export interface AnalyzeRouteBody {
  coordinates: number[][];
  alternatives: number[][][];
  waypoints: number[][];
  congestion: (number | null)[];
  corridor_m: number;
  segment_m: number;
  lookback_hours: number;
}

/**
 * Score the chosen route against everything the platform knows near its
 * corridor. Costs no Mapbox quota — the planner already holds every input.
 */
export async function analyzeRoute(
  body: AnalyzeRouteBody,
  signal?: AbortSignal,
): Promise<RouteRiskReport> {
  const response = await axios.post(`${baseUrl}/routing/analyze`, body, {
    headers: authHeaders(),
    signal,
  });
  return response.data;
}

export interface RouteAssetsBody {
  coordinates: number[][];
  service_radius_m?: number;
  segment_m?: number;
}

/**
 * Infrastructure on and beside the route, from OpenStreetMap.
 *
 * Kept off `analyzeRoute` on purpose: this one leaves the platform, so the
 * corridor scoring we compute ourselves is never held up behind it.
 */
export async function fetchRouteAssets(
  body: RouteAssetsBody,
  signal?: AbortSignal,
): Promise<RouteAssetsReport> {
  const response = await axios.post(`${baseUrl}/routing/assets`, body, {
    headers: authHeaders(),
    signal,
  });
  return response.data;
}

export interface RouteEventsBody {
  coordinates: number[][];
  depart_at?: string | null;
  duration_s?: number | null;
  corridor_m?: number;
  horizon_hours?: number;
  window_start?: string | null;
  window_end?: string | null;
}

/**
 * Scheduled disruptions near the route, found by web search and read by the
 * model. Slower than every other call here — a search plus two model round
 * trips — so it is opt-in and never on the path to a first route.
 */
export async function fetchRouteEvents(
  body: RouteEventsBody,
  signal?: AbortSignal,
): Promise<RouteEventsReport> {
  const response = await axios.post(`${baseUrl}/routing/events`, body, {
    headers: authHeaders(),
    signal,
  });
  return response.data;
}

export interface SavedRoute {
  id: number;
  name: string;
  profile: string;
  options: Record<string, unknown>;
  waypoints: Array<{ longitude: number; latitude: number; label?: string }>;
  geometry: GeoJSON.LineString | null;
  stats: Record<string, number>;
  mission_id: number | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export async function listSavedRoutes(missionId?: number): Promise<SavedRoute[]> {
  const response = await axios.get(`${baseUrl}/routing/routes`, {
    headers: authHeaders(),
    params: missionId ? { mission_id: missionId } : undefined,
  });
  return response.data;
}

export async function saveRoute(body: {
  name: string;
  profile: string;
  options: Record<string, unknown>;
  waypoints: Array<{ longitude: number; latitude: number; label?: string }>;
  geometry: GeoJSON.LineString | null;
  stats: Record<string, number>;
  mission_id?: number | null;
}): Promise<SavedRoute> {
  const response = await axios.post(`${baseUrl}/routing/routes`, body, { headers: authHeaders() });
  return response.data;
}

export async function updateSavedRoute(routeId: number, body: Parameters<typeof saveRoute>[0]): Promise<SavedRoute> {
  const response = await axios.put(`${baseUrl}/routing/routes/${routeId}`, body, { headers: authHeaders() });
  return response.data;
}

export async function deleteSavedRoute(routeId: number): Promise<void> {
  await axios.delete(`${baseUrl}/routing/routes/${routeId}`, { headers: authHeaders() });
}
